import {
  DEFAULT_LOCALE,
  mapLocaleWovnToStrapi,
  mapLocaleWovnToTripla,
} from "@/i18n/routing";
import { ISelectionResponse } from "@/types/mice";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { getPublicEnv } from "@/utils/env";
import crypto from 'crypto';

/**
 * Utility helpers for link handling, locale mapping, class merging,
 * URL transformation, and selection sorting/filtering.
 *
 * These utilities are shared across the application and deal with:
 * - WOVN locale mapping to Strapi and Tripla
 * - Generating internal/external/anchor/email links
 * - Sorting and filtering CMS selection data
 * - Flattening and parsing short URL strings or HTML inputs
 */

// Merge multiple Tailwind class names safely
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Retrieve token from localStorage if available (SSR-safe)
export const getTokenFromStorage = () => {
  if (globalThis.window === undefined) {
    return { token: "" };
  }
  const token = localStorage.getItem("token") ?? "";
  return { token };
};

// Enum types for differentiating link behavior
export enum LinkType {
  EXTERNAL = "external",
  INTERNAL = "internal",
  ANCHOR = "anchor",
  EMAIL = "email",
  JAVASCRIPT = "javascript",
  TEXT = "text",
}

export enum LinkTarget {
  BLANK = "_blank",
}

interface BaseMetadata {
  target?: LinkTarget | string;
  title?: string;
  text?: string;
}

interface ExternalLinkMetadata extends BaseMetadata {
  url: string;
}

interface InternalLinkMetadata extends BaseMetadata {
  uid?: string;
  url: string;
  documentId?: string;
}

interface AnchorLinkMetadata extends BaseMetadata {
  text: string;
  anchor: string;
}

interface EmailLinkMetadata extends BaseMetadata {
  email: string;
  text?: string;
}

interface JavascriptLinkMetadata extends BaseMetadata {
  url: string;
}

interface TextMetadata extends BaseMetadata {
  text: string;
}

type LinkMetadata =
  | ExternalLinkMetadata
  | InternalLinkMetadata
  | AnchorLinkMetadata
  | EmailLinkMetadata
  | JavascriptLinkMetadata;

export interface LinkConfig {
  type: LinkType;
  metadata: LinkMetadata;
}

// Map WOVN locale cookie to Strapi locale format
export function getLocaleWOWNSelected(): string {
  if (globalThis.window === undefined) {
    return DEFAULT_LOCALE;
  }

  const regex = /wovn_selected_lang=([^;]+)/;
  const match = regex.exec(document.cookie);
  const locale = match ? match[1] : DEFAULT_LOCALE;

  return mapLocaleWovnToStrapi(locale);
}

// Map WOVN locale cookie to Tripla format
export function getLocaleTriplaByWOVN(): string {
  if (globalThis.window === undefined) {
    return DEFAULT_LOCALE;
  }

  const regex = /wovn_selected_lang=([^;]+)/;
  const match = regex.exec(document.cookie);
  const locale = match ? match[1] : DEFAULT_LOCALE;

  return mapLocaleWovnToTripla(locale);
}

// Converts brand URLs from Strapi to their internal app route format.
export const mapBrandInternalUrl = (url: string): string => {
  const kamenoiCollectionUrl = "/brands/mystays-collection";
  const iconiaCollectionUrl = "/brands/iconia-collection";
  const artHotelUrl = "/brands/art-hotel";
  const flexStayUrl = "/brands/flexstay";
  const myCubeUrl = "/brands/mycube";
  const kamenoiHotelUrl = "/brands/kamenoi-hotel";
  switch (url) {
    case kamenoiCollectionUrl:
    case iconiaCollectionUrl:
      url = "/hotels-iconia-collection";
      break;
    case artHotelUrl:
      url = "/hotels-art-hotel";
      break;
    case flexStayUrl:
      url = "/hotels-flexstay";
      break;
    case myCubeUrl:
      url = "/hotels-mycube";
      break;
    case kamenoiHotelUrl:
      url = "/hotels-kamenoi-hotel";
      break;
    default:
      break;
  }
  return url;
};

// Centralized reference for brand URLs
export const BRAND_URLS = {
  kamenoiHotelUrl: "/hotels-kamenoi-hotel/",
  kamenoiCollectionUrl: "/hotels-kamenoi-collection/",
  artHotelUrl: "/hotels-art-hotel",
  flexStayUrl: "/hotels-flexstay",
  myCubeUrl: "/hotels-mycube",
  mystaysPremierUrl: "/hotels-mystays-premier/",
  mystaysUrl: "/hotels-mystays/",
} as const;

// Remove leading and trailing slashes without using a backtracking-prone regex.
// This is intentionally iterative and linear-time to avoid ReDoS vectors.
export function stripSlashes(input?: string | null): string {
  if (!input) return "";
  let s = input;
  // Remove leading slashes
  while (s.startsWith("/")) {
    s = s.slice(1);
  }
  // Remove trailing slashes
  while (s.endsWith("/")) {
    s = s.slice(0, -1);
  }
  return s;
}

/**
 * Generate the appropriate URL based on link configuration type.
 * Handles internal localization, brand mapping, anchors, and external links.
 */
export const generateLinkFromGeneralLink = (
  linkConfig: LinkConfig,
  currentLocale: string,
): string => {
  if (!linkConfig?.type) {
    return "#";
  }
  const { type, metadata } = linkConfig;
  const getCurrentPath = () => {
    if (globalThis.window !== undefined) {
      return globalThis.location.pathname;
    }
    return "";
  };
  const currentPath = getCurrentPath();
  const locale = currentLocale || DEFAULT_LOCALE;
  switch (type) {
    case LinkType.EXTERNAL:
      return (metadata as ExternalLinkMetadata).url;
    case LinkType.INTERNAL: {
      const internalMeta = metadata as InternalLinkMetadata;
      const remapBrandLink = mapBrandInternalUrl(internalMeta?.url || "");
      const cleanPath = stripSlashes(remapBrandLink);
      return `/${locale}/${cleanPath}`;
    }
    case LinkType.ANCHOR: {
      const anchorMeta = metadata as AnchorLinkMetadata;
      return `${currentPath}#${anchorMeta.text}`;
    }
    case LinkType.EMAIL: {
      const emailMeta = metadata as EmailLinkMetadata;
      return emailMeta.email;
    }
    case LinkType.JAVASCRIPT: {
      return `${currentPath}#`;
    }
    case LinkType.TEXT:
      return (metadata as TextMetadata).text;
    default:
      return `#`;
  }
};

// Returns valid HTML link target, defaults to `_parent` for safety
export const generateTargetFromLink = (
  linkConfig: LinkConfig,
): "_self" | "_blank" | "_parent" | "_top" => {
  const target = linkConfig?.metadata?.target;

  if (
    target === "_self" ||
    target === "_blank" ||
    target === "_parent" ||
    target === "_top"
  ) {
    return target;
  }

  return "_parent";
};

// Splits a bed type string into text and numeric parts
export function splitBedType(str: string): [string, string] {
  if (!str) {
    return ["", ""];
  }
  // If the string starts with a digit, return ["", str]
  const firstCode = str.codePointAt(0) ?? -1;
  if (firstCode >= 48 && firstCode <= 57) {
    return ["", str];
  }

  // Find the index of the first digit using a linear scan to avoid regex backtracking.
  let idx = -1;
  for (let i = 0; i < str.length; i++) {
    const code = str.codePointAt(i) ?? -1;
    if (code >= 48 && code <= 57) {
      idx = i;
      break;
    }
  }

  if (idx !== -1) {
    return [str.slice(0, idx).trim(), str.slice(idx).trim()];
  }

  return [str, ""];
}

// Sorts an array of items based on order defined in selectionList.
export const sortSelectionData = <T extends { documentId?: string }>(
  list: T[],
  selectionList: ISelectionResponse[],
) => {
  if (!list || !Array.isArray(list) || list.length === 0 || !selectionList)
    return [];

  return list.toSorted((a, b) => {
    const orderA =
      selectionList?.find(
        (item: { documentId: string; order?: number }) =>
          item.documentId === a?.documentId,
      )?.order || 0;

    const orderB =
      selectionList?.find(
        (item: { documentId: string; order?: number }) =>
          item.documentId === b?.documentId,
      )?.order || 0;

    return orderA - orderB;
  });
};

/**
 * Filters a list to only include items present in selectionList,
 * preserving their original selection order.
 */
export const filterSelectionData = <T extends { documentId?: string }>(
  list: T[],
  selectionList: ISelectionResponse[],
) => {
  if (!list || !Array.isArray(list) || list.length === 0 || !selectionList)
    return [];
  return sortSelectionData(list, selectionList)?.filter((item) =>
    selectionList?.some((sel) => sel?.documentId === item?.documentId),
  );
};

// Detects if any URL path segment indicates a file or invalid hotel route.
export function isNotHotelLinkFormat(segments: string[]): boolean {
  const fileExtensions = [
    // Documents
    ".pdf",
    ".doc",
    ".docx",
    ".xls",
    ".xlsx",
    ".ppt",
    ".pptx",
    ".txt",
    ".rtf",
    ".odt",

    // Images
    ".jpg",
    ".jpeg",
    ".png",
    ".gif",
    ".bmp",
    ".tiff",
    ".tif",
    ".svg",
    ".webp",
    ".ico",
    ".heic",

    // Videos
    ".mp4",
    ".avi",
    ".mov",
    ".wmv",
    ".flv",
    ".mkv",
    ".webm",
    ".3gp",
    ".mpeg",
    ".mpg",

    // Audio
    ".mp3",
    ".wav",
    ".aac",
    ".flac",
    ".ogg",
    ".m4a",
    ".wma",
    ".aiff",
    ".alac",

    // Archives & Compressed
    ".zip",
    ".rar",
    ".7z",
    ".tar",
    ".gz",
    ".bz2",
    ".xz",
    ".iso",

    // Code / Scripts
    ".js",
    ".ts",
    ".jsx",
    ".tsx",
    ".html",
    ".css",
    ".scss",
    ".json",
    ".xml",
    ".yml",
    ".yaml",
    ".py",
    ".java",
    ".c",
    ".cpp",
    ".h",
    ".cs",
    ".php",
    ".rb",
    ".go",
    ".sh",
    ".bat",
    ".sql",

    // Executables / System
    ".exe",
    ".msi",
    ".dll",
    ".bin",
    ".apk",
    ".jar",
    ".deb",
    ".rpm",
    ".sys",

    // Fonts
    ".ttf",
    ".otf",
    ".woff",
    ".woff2",
    ".eot",

    // Design / CAD
    ".psd",
    ".ai",
    ".xd",
    ".sketch",
    ".fig",
    ".dwg",
    ".dxf",

    // Other file types
    ".csv",
    ".log",
    ".dat",
    ".bak",
    ".tmp",
    ".md",
  ];

  return segments.some(
    (segment) =>
      segment === "-" ||
      segment === "~" ||
      segment === "_" ||
      fileExtensions.some((ext) => segment.toLowerCase().includes(ext)),
  );
}

/**
 * Normalizes and flattens "short URL" data which may come as plain text or HTML.
 * Extracts all unique clean path strings.
 */
export function flattenShortUrlArray(
  data: { documentId: string; slug: string; chunks: string }[],
): { documentId: string; slug: string; paths: string[] }[] {
  return data.map(({ documentId, slug, chunks }) => {
    const urls = new Set<string>();

    // Case 1: Raw string separated by break lines and whitespace
    if (!chunks.includes("<") && !chunks.includes(">")) {
      // Simple check for non-HTML
      for (const part of chunks.split(/\s+/)) {
        const trimmed = part.trim();
        if (trimmed) {
          urls.add(stripSlashes(trimmed));
        }
      }
    } else {
      // Case 2: HTML string - use regex to strip HTML tags and extract text
      // This approach avoids external dependencies that may cause build issues
      const textContent = chunks
        .replaceAll(/<script[^>]*>[\s\S]*?<\/script>/gi, "") // Remove script tags
        .replaceAll(/<style[^>]*>[\s\S]*?<\/style>/gi, "") // Remove style tags
        .replaceAll(/<[^<>]+>/g, " ") // Remove all HTML tags
        .replaceAll(" ", " ") // Replace   with space
        .replaceAll(/&[a-z]+;/gi, " "); // Replace other entities

      for (const part of textContent.split(/\s+/)) {
        const trimmed = part.trim();
        if (trimmed) {
          urls.add(stripSlashes(trimmed));
        }
      }
    }

    return {
      documentId,
      slug,
      paths: Array.from(urls),
    };
  });
}

export function getRumUrl(): string {
  const region = getPublicEnv("NEXT_PUBLIC_AWS_REGION");
  return `https://dataplane.rum.${region}.amazonaws.com`;
}
export const hotelOverviewPatchPattern =
  /^hotel-([A-Za-z0-9_-]{1,100})-([A-Za-z0-9_-]{1,100})$/;
export const hotelDetailPatchPattern = {
  locationPattern: /^location-([A-Za-z0-9_-]{1,100})-([A-Za-z0-9_-]{1,200})$/,
  restaurantPattern:
    /^restaurants-([A-Za-z0-9_-]{1,100})-([A-Za-z0-9_-]{1,200})$/,
  //note:will do it later handle guest room later
  roomPattern: /^rooms-([A-Za-z0-9_-]{1,100})-([A-Za-z0-9_-]{1,200})$/,
  banquetPattern: /^banquets-([A-Za-z0-9_-]{1,100})-([A-Za-z0-9_-]{1,200})$/,
  facilitiesPattern:
    /^facilities-([A-Za-z0-9_-]{1,100})-([A-Za-z0-9_-]{1,200})$/,
  onsenPattern: /^onsen-hotel-([A-Za-z0-9_-]{1,100})-([A-Za-z0-9_-]{1,200})$/,
  poolSpaPattern:
    /^pool-spa-hotel-([A-Za-z0-9_-]{1,100})-([A-Za-z0-9_-]{1,200})$/,
  activityPattern: /^activity-([A-Za-z0-9_-]{1,100})-([A-Za-z0-9_-]{1,200})$/,
};

export const getEntityCodeAndAcquisitionChannel = (
  propertyid: any,
  qrcode: any,
  acqChannel: any,
  baseEntityCode: any,
  acqChannelCookie: any
) => {
  let acquisitionChannel;

  if (acqChannel) {
    baseEntityCode = propertyid ?? baseEntityCode;
    acquisitionChannel = acqChannel;
  } else if (qrcode) {
    baseEntityCode = propertyid;
    acquisitionChannel = "Mystays Website with QR";
  } else {
    baseEntityCode = propertyid ?? baseEntityCode;
    acquisitionChannel = acqChannelCookie ? acqChannelCookie : "Mystays Website";
  }

  return {
    entityCode: `${baseEntityCode}`,
    acquisitionChannel,
  };
};

export const extractTrackingParams = (search: string) => {
  const utmParams: Record<string, string> = {};
  let acquisitionChannel: string | undefined;

  if (!search) {
    return {
      utmParams,
      acquisitionChannel,
    };
  }

  const query = search.startsWith("?")
    ? search.slice(1)
    : search;

  const params = query.split("&");

  params.forEach((param) => {
    const [key, value] = param.split("=");

    if (!key || !value) {
      return;
    }

    const decodedValue = decodeURIComponent(value);

    if (key.startsWith("utm_")) {
      utmParams[key] = decodedValue;
    }

    if (key === "acquisitionChannel") {
      acquisitionChannel = decodedValue;
    }
  });

  return {
    utmParams,
    acquisitionChannel,
  };
};

function isCloudflareUrl(src: string) {
  const cloudflareHost =
    process.env.NEXT_PUBLIC_CLOUDFLARE_IMAGE_HOST || "imagedelivery.net";
  return src.includes(cloudflareHost);
}

export const localCloudflareLoader =
  (width?: number, quality?: number) =>
    ({ src }: { src: string }): string => {
      // Get default quality from env, fallback to 70
      if (!isCloudflareUrl(src)) {
        return src;
      }
      const url = new URL(src);
      const pathParts = url.pathname.split("/");
      if (pathParts.length >= 4) {
        pathParts.pop();
      }

      const imageQuality = quality || 75;

      const variant = `w=${width},q=${imageQuality},format=auto`;
      // Reconstruct the URL
      return `${url.origin}${pathParts.join("/")}/${variant}`;
    };

export const sanitizeDescription = (description: string): string => {
  return description.replace(/^<p>/, "").replace(/<\/p>$/, "");
};

export const convertTimeToSeconds = (time: string) => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 3600 + minutes * 60;
};

export const formatDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};
export const hasValidContent = (html: string | null | undefined): boolean => {
  if (!html) return false;

  // 1. Remove unwanted tags
  const cleaned = html
    .replace(/<style[\s\S]*?<\/style>/gi, "") // remove styles
    .replace(/<head[\s\S]*?<\/head>/gi, "") // remove head
    .replace(/<script[\s\S]*?<\/script>/gi, "") // remove scripts
    .replace(/<[^>]+>/g, "") // remove all HTML tags
    .replace(/&nbsp;/gi, "") // remove &nbsp;
    .trim();

  // 2. Check if anything meaningful remains
  return cleaned.length > 0;
};

export function gtpIdToHash(gtId: any) {
  if (!gtId) {
    return '';
  }
  const salt = crypto.createHash('sha256').update('297').digest('hex');
  return crypto.createHash('sha256').update(salt + gtId).digest('hex');
}